import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import { cleanCollisions, continuousExtentParts } from '../chart_helpers/AxisHelper'
import MirrorAxisHelper from '../chart_helpers/MirrorAxisHelper'
import SizeHelper from '../chart_helpers/SizeHelper'
import { categoricalExtent, continuousExtent } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import { tweenDentedRect } from '../utils/pathUtils'
import { select as d3_select } from 'd3-selection'
import { interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'

const MAX_BAR_WIDTH = 40
export default class PyramidChartChart extends BaseChart {
  constructor(...args) {
    super(...args)
    this.zeroBased = true
  }
  static defaultConfig = {
    /**
     * {-EN-} Orientation of the bars. When setting `horizontal: true` the x and y axes will be swapped.
     * {-ES-} Orientación de las barras. Al configurar `horizontal: true`, los ejes x e y se intercambiarán.
     */
    horizontal: {
      type: Boolean,
      default: true,
      exposed: true,
      nonEditable: true,
    },
    mode: {
      type: Boolean,
      default: true,
      exposed: true,
      nonEditable: true,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    /**
     * {-EN-} Show the value on top of each bar.
     * {-ES-} Muestra el valor encima de cada barra.
     */
    showDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
      nonEditable: true,
    },
    /**
     * {-EN-} Minimum height of each bar (in pixels).
     * {-ES-} Altura mínima de cada barra (en píxeles).
     */
    minBarHeight: {
      type: [Number],
      default: 0,
      exposed: true,
    },
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
    AxisHelper: {
      zoom: {
        nonEditable: true,
      },
      scroll: {
        nonEditable: true,
      },
      showScrollbar: {
        nonEditable: true,
      },
      x: {
        dimensions: 'male-and-female',
        scale: {
          value: 'band',
          exposed: false,
        },
        getExtent: function (data) {
          return categoricalExtent(data[0].value)
          /*
          const maxValue = data.reduce((accumulator, current) => {
            const values = Object.values(current.dimensions)
            values[0] > accumulator && (accumulator = values[0])
            values[1] > accumulator && (accumulator = values[1])
            return accumulator
          }, 0)
          return [0, maxValue]
          */
        },
        showTickValues: {
          nonEditable: true,
        },
        tickOrientation: {
          nonEditable: true,
        },
      },
      y: {
        side: 'left',
        getExtent: function (data) {
          let extent, extentParts

          extentParts = continuousExtentParts(data)
          // console.log("extent parts",extentParts)
          extent = continuousExtent(extentParts)

          //Protect label!
          // let originalExtent = extent

          // extent = padLinear(extent,0.2)

          // NOT GOOD, porque se hace el padlinear luego, en el _setDomain
          // if(originalExtent[0]>=0) extent[0]=0
          // console.log("bar extent bef",extent)
          if (this.config.AxisHelper.y.showTickValues && this.config.AxisHelper.y.unit) {
            extent = cleanCollisions(this.axes.y_0, extent, extentParts)
          }
          // console.log("bar extent aft",extent)
          return extent
        },
      },
    },
    TooltipBehavior: {
      //itemsKeys: ['maleItems', 'femaleItems'],
      getItemOffset: function ($el) {
        let original = $el.getBoundingClientRect()
        return {
          x: original.x + original.width / 2,
          y: original.top, //- 5
        }
      },
      getHtml: function (d, itemsKey) {
        let onAxis = itemsKey === 'maleItems' ? 'y_0' : 'y_1'
        if (itemsKey === 'maleItems') {
          return `
            <div class="tip-value important">${this.valueWithUnit(d.value, onAxis)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: d.parentId },
                0
              )}; margin-right: 3px"></span>
              ${d.parentId}
            </div>
            <div class="secondary">${this.formatValue(d.id, 'x')}</div>
            `
        } else {
          return `
            <div class="tip-value important">${this.valueWithUnit(d.value, onAxis)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: d.id },
                1
              )}; margin-right: 3px"></span>
              ${d.id}
            </div>
            `
        }

        /*return `
          <span class="important">${this.valueWithUnit(d.value, onAxis)}</span>
          <br/>
          ${d.id}
        `*/
      },
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, MirrorAxisHelper]
  checkConsistency() {
    return true
  }
  draw() {
    const parentIds = this.data.reduce((accumulator, current) => {
      if (!accumulator.includes(current.id)) accumulator.push(current.id)
      return accumulator
    }, [])
    const self = this
    let longDir, getTranslate, getOffset, getShort, offsetValue, barWidth
    longDir = 'height'
    // shortDir = 'width'
    getTranslate = (long, short) => {
      return `translate(${short},${long})`
    }

    if (this.axes.y_0.func.scale().domain()[0] < 0 && this.axes.y_0.func.scale().domain()[1] > 0)
      offsetValue = 0
    else offsetValue = this.axes.y_0.func.scale().domain()[0]

    // getOffset = v => Math.min(v, this.axes.y_0.func.scale()(offsetValue))
    getOffset = (v, axis) => {
      if (this.axes[axis].func.scale().domain()[0] === 0)
        return Math.min(v, this.axes[axis].func.scale()(offsetValue))
      else return this.axes[axis].func.scale()(offsetValue)
    }
    // getShort = v => Math.abs(this.axes.y_0.func.scale()(offsetValue) - v)
    getShort = (v, axis) => {
      let val = this.axes[axis].func.scale()(offsetValue) - v
      return Math.abs(val)
    }

    let barPercent = 0.7
    barWidth = Math.min(MAX_BAR_WIDTH, this.axes.x.scale.bandwidth() * barPercent)
    let barMargin = (this.axes.x.scale.bandwidth() - barWidth) / 2

    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    let drawItem

    drawItem = (sel, status) => {
      sel.attr('transform', d => {
        const parentIdIndex = parentIds.findIndex(parentId => parentId === d.parentId)
        const axis = `y_${parentIdIndex}`
        let barPos
        if (status === 'enter' || status === 'exit')
          barPos = getOffset(this.axes[axis].func.scale()(0), axis)
        else barPos = getOffset(this.axes[axis].func.scale()(d.value), axis)
        return getTranslate(this.axes.x.func.scale()(d.id) + barMargin, barPos)
      })

      if (sel.select('path').attrTween) {
        sel.select('path').attrTween('d', function (d) {
          const parentIdIndex = parentIds.findIndex(parentId => parentId === d.parentId)
          const axis = `y_${parentIdIndex}`
          let barLength
          if (status === 'enter' || status === 'exit') barLength = 0
          else barLength = getShort(self.axes[axis].func.scale()(d.value), axis)
          // let w = longDir === 'width' ? barWidth : barLength
          // let h = longDir === 'width' ? barLength : barWidth
          // let dent =
          //   longDir === 'width'
          //     ? d.safeValue > 0
          //       ? ['tl', 'br']
          //       : 'bl'
          //     : d.safeValue > 0
          //     ? ['bl', 'tr']
          //     : 'tr'

          let gap = null
          if (
            self.axes[axis].func.scale().isChunky &&
            d.safeValue > self.axes[axis].func.scale()._threshold
          ) {
            gap =
              self.axes[axis].func.scale()(self.axes[axis].func.scale()._threshold) -
              self.axes[axis].func.scale()(d.safeValue)
          }
          return tweenDentedRect.call(this, d, {
            barLength,
            barWidth,
            longDir,
            scale: self.axes[axis].func.scale(),
            gapStart: gap,
          })
          // return dentedRect(w, h, dent, gap)
        })
        sel.select('path').attrTween('fill', function (d) {
          return fillTween.bind(this)({ ...d, id: d.parentId })
        })
      }
    }

    this['itemsEnter'].append('path')

    // HAy que pintar lo "viejos" primero para assegurar que se mantiene el orden de colores...
    this['itemsTrans'].call(drawItem)
    this['itemsEnter'].call(drawItem, 'enter')
    this['itemsEnterTrans'].call(drawItem)
    this['itemsExitTrans'].style('opacity', 0)
    if (this['parentItemsExitTrans']) this['parentItemsExitTrans'].style('opacity', 0)

    // let datumValues = this.maleItemsEnter
    //   .merge(this.maleItems)
    //   .selectAll('.datum-value')
    //   .data(d => {
    //     if (this.config.showDatumValues) {
    //       if (d.isLastLayer) {
    //         //chapuzilla raruna (el safeVAlue este, para stacked+horizontal).. pero va...
    //         return [{ value: d.y1, safeValue: d.y1 - d.y0 }]
    //       } else {
    //         return []
    //       }
    //     } else {
    //       return []
    //     }
    //   })
    // datumValues.exit().transition().style('opacity', 0).remove()
    // datumValues = datumValues.merge(
    //   datumValues
    //     .enter()
    //     .append('text')
    //     .attr('class', 'datum-value')
    //     .style('opacity', 0)
    //     .style('font-size', '14px')
    //     .style('font-weight', 'bold')
    //     .attr('fill', 'currentColor')
    // )
    // datumValues
    //   .text(d => this.formatValue(d.value, 'y'))
    //   // .text(d => this.valueWithUnit(d.value, 'y'))
    //   .transition()
    //   .style('opacity', 1)
    // if (this.config.horizontal) {
    //   datumValues
    //     .attr('dominant-baseline', 'middle')
    //     .attr('text-anchor', 'start')
    //     .attr('y', 2 + (this.config.mode === 'grouped' ? subBarWidth / 2 : barWidth / 2))
    //     .attr('x', d => getShort(this.axes.y_0.func.scale()(d.safeValue)) + 8)
    // } else {
    //   datumValues
    //     .attr('text-anchor', 'middle')
    //     .attr('x', this.config.mode === 'grouped' ? subBarWidth / 2 : barWidth / 2)
    //     .attr('y', -8)
    // }

    // this.maleItemsEnter.append('text').attr('text-anchor', 'middle')
    // this.maleItems
    //   .merge(this.maleItemsEnter)
    //   .select('text')
    //   // .text(d => this.formatValue(d.value, 'y'))
    //   .text(d => this.valueWithUnit(d.value, 'y'))
    //   .attr('x', this.config.mode === 'grouped' ? subBarWidth / 2 : barWidth / 2)
    //   .attr('y', -8)
  }
}
